"use client";

import { create } from "zustand";

export type ToastType = "success" | "error" | "info" | "loading";

export interface ToastItem {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
}

interface ToastState {
  toasts: ToastItem[];
  addToast: (toast: Omit<ToastItem, "id"> & { id?: string }) => string;
  removeToast: (id: string) => void;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  addToast: (t) => {
    const id = t.id || Math.random().toString(36).substring(2, 9);
    set((state) => {
      const filtered = state.toasts.filter((item) => item.id !== id);
      return { toasts: [...filtered, { ...t, id }] };
    });
    if (t.duration !== 0 && t.type !== "loading") {
      const dur = t.duration || (t.type === "error" ? 6000 : 4000);
      setTimeout(() => {
        set((state) => ({ toasts: state.toasts.filter((item) => item.id !== id) }));
      }, dur);
    }
    return id;
  },
  removeToast: (id) =>
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

export const toast = {
  success: (message: string, opts?: { id?: string; title?: string; duration?: number }) =>
    useToastStore.getState().addToast({ type: "success", message, ...opts }),
  error: (message: string, opts?: { id?: string; title?: string; duration?: number }) =>
    useToastStore.getState().addToast({ type: "error", message, ...opts }),
  info: (message: string, opts?: { id?: string; title?: string; duration?: number }) =>
    useToastStore.getState().addToast({ type: "info", message, ...opts }),
  loading: (message: string, opts?: { id?: string; title?: string }) =>
    useToastStore.getState().addToast({ type: "loading", message, duration: 0, ...opts }),
  dismiss: (id: string) => useToastStore.getState().removeToast(id),
};
